import {
  faExclamationCircle,
  faPlus,
  faXmark,
} from '@fortawesome/free-solid-svg-icons';
import { faDryer } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
/* eslint-disable react/prop-types -- TypeScript props enforce shapes; runtime prop-types are not used in this codebase */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { JobTemplateFieldsFragment } from '~/gql/graphql';
import { Asset, AssetSelect } from '../AssetSelect/AssetSelect';
import { Accordion } from '../ui/Accordion';
import { PopupDialog } from '../ui/PopupDialog';
import {
  calculateTaskPreviews,
  type TaskPreview,
  type TaskPreviewAsset,
} from './calculateTaskPreviews';

export {
  calculateTaskPreviews,
  generateTasksFromAssetDrivenTemplate,
} from './calculateTaskPreviews';

type Props = {
  /** The selected asset-driven template */
  template: JobTemplateFieldsFragment;
  /** All available assets (used to resolve selectedAssetIds) */
  allAssets: Asset[];
  /** Assets that can be selected or added (defaults to allAssets).
   *  Used to filter available options in AssetSelect and Add Assets dialog */
  availableAssets?: Asset[];
  /** Selected asset IDs */
  selectedAssetIds: string[];
  /** Callback to update selected assets */
  onAssetSelectionChange?: (assetIds: string[]) => void;
  /** Callback to open the tasks list (not used when renderAssetsSeparately is true) */
  onViewTasks?: () => void;
  /** Callback to create a task for an asset that doesn't match template rules */
  onCreateAssetTask?: (assetId: string) => void;
  /** Default location ID for asset selection (filters AssetSelect) */
  defaultLocationId?: string;
  /** Variant to control UI behavior:
   *  - 'standard': Shows template selection UI, used in normal job creation flow
   *  - 'bulk': Hides template selection, used when coming from bulk asset edit view */
  variant?: 'standard' | 'bulk';
  /** Asset IDs that already have tasks created manually in the form.
   *  These assets won't show "no matching tasks" warnings */
  coveredAssetIds?: string[];
  /** When true, skips rendering the assets list inline.
   *  Parent should render AssetDrivenAssetsSection separately for better layout control.
   *  Used in CreateJobForm to position assets section in a different accordion section */
  renderAssetsSeparately?: boolean;
  /** For standard variant: when true, filters to show only assets without matching tasks.
   *  Used to show only problematic assets that need attention */
  showOnlyUncoveredAssets?: boolean;
};

/**
 * Shared AssetCard component for displaying asset information
 * Used by both AssetDrivenJobPreview and AssetDrivenAssetsSection to avoid duplication
 */
type AssetCardProps = {
  asset: TaskPreviewAsset;
  matchingTasks?: TaskPreview[];
  hasManualCoverage?: boolean;
  onCreateAssetTask?: (assetId: string) => void;
  onRemoveAsset?: (assetId: string) => void;
  variant?: 'withTasks' | 'withoutTasks' | 'streamlined';
};

const AssetCard = ({
  asset,
  matchingTasks = [],
  hasManualCoverage = false,
  onCreateAssetTask,
  onRemoveAsset,
  variant = 'withTasks',
}: AssetCardProps) => {
  const { t } = useTranslation();

  // Streamlined variant for bulk asset flow - just name and remove button
  if (variant === 'streamlined') {
    return (
      <div className='flex items-center justify-between gap-3 rounded border border-outline-variant bg-white px-3 py-2 shadow-sm'>
        <span className='truncate font-medium text-primary'>{asset.name}</span>
        {onRemoveAsset && (
          <button
            type='button'
            onClick={() => onRemoveAsset(asset.id)}
            className='text-grey-40 transition-colors hover:text-red-600'
            title={t('job:assetDrivenTemplate.removeAsset', 'Remove asset')}
          >
            <FontAwesomeIcon icon={faXmark} className='h-4 w-4' />
          </button>
        )}
      </div>
    );
  }

  const isWarningVariant = variant === 'withoutTasks';
  const borderClass = isWarningVariant
    ? 'border-amber-200 bg-amber-50'
    : 'border-outline-variant bg-white shadow-sm';

  // Check if any task used the "Other" rule
  const usedOtherRule = matchingTasks.some((task) => task.usedOtherRule);

  return (
    <div
      className={`flex items-center justify-between gap-3 rounded border px-3 py-2.5 ${borderClass}`}
    >
      <div className='flex min-w-0 flex-1 flex-col'>
        <div className='flex items-center gap-2'>
          <span className='truncate font-medium text-primary'>
            {asset.name}
          </span>
          <span className='shrink-0 text-xs text-tertiary'>{asset.type}</span>
        </div>
        {isWarningVariant ? (
          <div className='flex items-center gap-1.5'>
            <FontAwesomeIcon
              icon={faExclamationCircle}
              className='h-3 w-3 text-amber-600'
            />
            <span className='text-xs text-amber-700'>
              {t(
                'job:assetDrivenTemplate.noMatchingTasks',
                'No matching tasks'
              )}
            </span>
          </div>
        ) : (
          <div className='flex flex-col gap-0.5'>
            {hasManualCoverage && (
              <span className='text-xs text-secondary'>
                {t(
                  'job:assetDrivenTemplate.manualTasksAdded',
                  'Task added manually'
                )}
              </span>
            )}
            {usedOtherRule && (
              <span className='text-xs text-amber-700'>
                {t(
                  'job:assetDrivenTemplate.usedOtherRule',
                  "Couldn't find a task rule for this asset type, used the 'Other' task rule. Check this works for this asset."
                )}
              </span>
            )}
          </div>
        )}
      </div>
      <div className='flex shrink-0 items-center gap-2'>
        {isWarningVariant && onCreateAssetTask && (
          <button
            type='button'
            onClick={() => onCreateAssetTask(asset.id)}
            className='hover:bg-brand/5 rounded border border-brand bg-white px-3 py-1.5 text-sm font-medium text-brand transition-colors'
          >
            {t('job:assetDrivenTemplate.createTask', 'Create Task')}
          </button>
        )}
        {onRemoveAsset && (
          <button
            type='button'
            onClick={() => onRemoveAsset(asset.id)}
            className='text-grey-40 transition-colors hover:text-red-600'
            title={t('job:assetDrivenTemplate.removeAsset', 'Remove asset')}
          >
            <FontAwesomeIcon icon={faXmark} className='h-4 w-4' />
          </button>
        )}
      </div>
    </div>
  );
};

/**
 * AssetDrivenJobPreview - Preview component for asset-driven job creation
 *
 * This component is used in two different contexts:
 *
 * 1. STANDARD VARIANT (variant='standard'):
 *    - Used in CreateJobForm for normal job creation flow
 *    - Shows template selection UI with AssetSelect component
 *    - When renderAssetsSeparately=true, only renders template selection section
 *    - Assets section is rendered separately via AssetDrivenAssetsSection component
 *    - Typically shows only uncovered assets (showOnlyUncoveredAssets=true)
 *
 * 2. BULK VARIANT (variant='bulk'):
 *    - Used in CreateJobForm when coming from bulk asset edit view
 *    - Does NOT show template selection UI (assets already pre-selected)
 *    - When renderAssetsSeparately=true, renders nothing (assets handled separately)
 *    - Assets section is rendered separately via AssetDrivenAssetsSection component
 *    - Shows all assets (both with and without tasks)
 *
 * Props:
 * - renderAssetsSeparately: When true, skips rendering the assets list inline.
 *   The parent should render AssetDrivenAssetsSection separately for better layout control.
 * - variant: Controls which UI elements are shown (template selection, asset filtering, etc.)
 * - showOnlyUncoveredAssets: For standard variant, filters to show only assets without matching tasks
 */
export const AssetDrivenJobPreview = ({
  template,
  allAssets,
  availableAssets,
  selectedAssetIds,
  onAssetSelectionChange,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onViewTasks: _onViewTasks, // Not used when renderAssetsSeparately is true
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onCreateAssetTask: _onCreateAssetTask, // Not used - only showing assets with Other rule tasks
  defaultLocationId,
  variant = 'standard',
  coveredAssetIds = [],
  renderAssetsSeparately = false,
  showOnlyUncoveredAssets = false,
}: Props) => {
  const { t } = useTranslation();
  const [showAddAssetsDialog, setShowAddAssetsDialog] = useState(false);
  const isBulkVariant = variant === 'bulk';
  const [dialogSelection, setDialogSelection] =
    useState<string[]>(selectedAssetIds);

  const selectableAssets = useMemo(
    () => availableAssets ?? allAssets,
    [allAssets, availableAssets]
  );
  const coveredAssetIdSet = useMemo(
    () => new Set(coveredAssetIds),
    [coveredAssetIds]
  );
  useEffect(() => {
    if (showAddAssetsDialog) {
      setDialogSelection(selectedAssetIds);
    }
  }, [selectedAssetIds, showAddAssetsDialog]);

  // Get selected assets from IDs
  const selectedAssets = useMemo(() => {
    const baseSelection = allAssets.filter((asset) =>
      selectedAssetIds.includes(asset.id)
    );

    if (baseSelection.length === selectedAssetIds.length) {
      return baseSelection;
    }

    // Fallback to selectable assets if some are missing from allAssets
    const existingIds = new Set(baseSelection.map((asset) => asset.id));
    const fallbackAssets = selectableAssets.filter(
      (asset) =>
        selectedAssetIds.includes(asset.id) && !existingIds.has(asset.id)
    );

    return [...baseSelection, ...fallbackAssets];
  }, [allAssets, selectableAssets, selectedAssetIds]);

  // Calculate task previews
  const { tasks, assetMatches } = useMemo(
    () => calculateTaskPreviews(template, selectedAssets),
    [template, selectedAssets]
  );

  // For standard variant with showOnlyUncoveredAssets, filter to only show uncovered assets
  const assetsToDisplay = useMemo(() => {
    if (showOnlyUncoveredAssets && !isBulkVariant) {
      // Only show assets that don't have tasks created for them
      return assetMatches.filter(
        (m) => !m.hasMatchingTasks && !coveredAssetIdSet.has(m.asset.id)
      );
    }
    // For bulk variant or when showing all assets, return all asset matches
    return assetMatches;
  }, [showOnlyUncoveredAssets, isBulkVariant, assetMatches, coveredAssetIdSet]);

  // Show only assets that have tasks created from the "Other" rule
  // Exclude assets that matched specific rules - we only want to show Other rule assets
  const displayedAssetsWithOtherRuleTasks = assetsToDisplay.filter(
    (m) =>
      m.matchingTasks.some((task) => task.usedOtherRule) &&
      !coveredAssetIdSet.has(m.asset.id)
  );

  // Handle removing an asset
  const handleRemoveAsset = (assetId: string) => {
    if (onAssetSelectionChange) {
      onAssetSelectionChange(selectedAssetIds.filter((id) => id !== assetId));
    }
  };

  const assetsForAddDialog = useMemo(() => {
    if (isBulkVariant && defaultLocationId) {
      return selectableAssets.filter(
        (asset) => asset.deployment?.location?.id === defaultLocationId
      );
    }

    return selectableAssets;
  }, [defaultLocationId, isBulkVariant, selectableAssets]);

  const toggleDialogAsset = useCallback((assetId: string) => {
    setDialogSelection((prev) =>
      prev.includes(assetId)
        ? prev.filter((id) => id !== assetId)
        : [...prev, assetId]
    );
  }, []);

  const handleDialogCancel = useCallback(() => {
    setDialogSelection(selectedAssetIds);
    setShowAddAssetsDialog(false);
  }, [selectedAssetIds]);

  const handleDialogSave = useCallback(() => {
    if (onAssetSelectionChange) {
      onAssetSelectionChange(dialogSelection);
    }
    setShowAddAssetsDialog(false);
  }, [dialogSelection, onAssetSelectionChange]);

  // Bulk variant shows full preview with "Add Assets" button
  const showFullPreview = isBulkVariant;

  return (
    <>
      {/* Template Selection Section - Only shown for standard variant */}
      {!isBulkVariant && (
        <Accordion.PanelSection>
          <div className='mb-3 flex items-center gap-2'>
            <FontAwesomeIcon icon={faDryer} className='h-5 w-5 text-brand' />
            <div className='text-base font-semibold text-primary'>
              {t('job:assetDrivenTemplate.selectAssets', 'Select Assets')}
            </div>
          </div>
          <p className='pb-3 text-sm text-secondary'>
            {t(
              'job:assetDrivenTemplate.selectAssetsDescription',
              'Tasks will be created for each selected asset that matches the template rules.'
            )}
          </p>
          <AssetSelect
            assets={selectableAssets}
            value={selectedAssets}
            onChange={(selected) => {
              if (onAssetSelectionChange) {
                onAssetSelectionChange(selected.map((a) => a.id));
              }
            }}
            multiple={true}
            enableFilters={true}
            defaultLocationId={defaultLocationId}
          />
          {selectedAssetIds.length > 0 && (
            <div className='mt-2 text-sm text-secondary'>
              {selectedAssetIds.length}{' '}
              {selectedAssetIds.length === 1 ? 'asset' : 'assets'} selected
            </div>
          )}
        </Accordion.PanelSection>
      )}

      {/* Assets List Section - Only rendered inline when renderAssetsSeparately is false.
          When renderAssetsSeparately=true, parent renders AssetDrivenAssetsSection separately */}
      {!renderAssetsSeparately && selectedAssetIds.length > 0 && (
        <Accordion.PanelSection>
          <div className='mb-2 flex items-center justify-between'>
            <p className='text-base font-semibold text-primary'>
              {isBulkVariant
                ? t('job:assetDrivenTemplate.assetsInJob', 'Assets in this Job')
                : t(
                    'job:assetDrivenTemplate.assetsWithOtherRule',
                    'Assets with Other Rule Tasks'
                  )}{' '}
              (
              {isBulkVariant
                ? selectedAssetIds.length
                : displayedAssetsWithOtherRuleTasks.length}
              )
            </p>
            {/* Helpful message when no tasks will be created */}
            {tasks.length === 0 &&
              coveredAssetIdSet.size === 0 &&
              selectedAssetIds.length > 0 && (
                <div className='mt-4 rounded-lg border border-amber-200 bg-amber-50 p-4'>
                  <p className='text-sm text-amber-800'>
                    {t(
                      'job:assetDrivenTemplate.noMatchingRules',
                      "None of the selected assets match the template's task rules. Try selecting different assets or use a different template."
                    )}
                  </p>
                </div>
              )}
            {showFullPreview && onAssetSelectionChange && (
              <div className='flex items-center gap-2'>
                <button
                  type='button'
                  onClick={() => setShowAddAssetsDialog(true)}
                  className='hover:bg-brand/5 flex items-center gap-1.5 rounded border border-brand bg-white px-3 py-1.5 text-sm font-medium text-brand transition-colors'
                >
                  <FontAwesomeIcon icon={faPlus} className='h-3.5 w-3.5' />
                  {t('job:assetDrivenTemplate.addAssets', 'Add Assets')}
                </button>
              </div>
            )}
          </div>

          <div className='space-y-2'>
            {/* Assets with tasks created from the "Other" rule - only show these */}
            {displayedAssetsWithOtherRuleTasks.map(
              ({ asset, matchingTasks }) => (
                <AssetCard
                  key={asset.id}
                  asset={asset}
                  matchingTasks={matchingTasks}
                  onRemoveAsset={
                    onAssetSelectionChange ? handleRemoveAsset : undefined
                  }
                  variant='withTasks'
                />
              )
            )}
          </div>

          {/* Hint message for assets with Other rule tasks */}
          {displayedAssetsWithOtherRuleTasks.length > 0 && (
            <p className='mt-2 text-xs text-secondary'>
              {t(
                'job:assetDrivenTemplate.assetsOtherRuleHint',
                'These assets matched the "Other" rule because they did not match any specific task rules.'
              )}
            </p>
          )}
        </Accordion.PanelSection>
      )}

      {/* Add Assets Dialog */}
      {showAddAssetsDialog && onAssetSelectionChange && (
        <PopupDialog
          isOpen={showAddAssetsDialog}
          onClose={handleDialogCancel}
          title={t('job:assetDrivenTemplate.addMoreAssets', 'Add More Assets')}
        >
          <div className='p-6'>
            <p className='mb-4 text-sm text-secondary'>
              {t(
                'job:assetDrivenTemplate.addMoreAssetsDescription',
                'Select additional assets to include in this job. Tasks will be created based on the template rules.'
              )}
            </p>
            <div className='max-h-[50vh] overflow-y-auto rounded border border-grey-10 bg-white p-2'>
              {assetsForAddDialog.length === 0 ? (
                <p className='text-sm text-secondary'>
                  {t(
                    'job:assetDrivenTemplate.noAssetsAtLocation',
                    'No assets are available at this location.'
                  )}
                </p>
              ) : (
                assetsForAddDialog.map((asset) => {
                  const isSelected = dialogSelection.includes(asset.id);
                  return (
                    <label
                      key={asset.id}
                      className='flex items-center justify-between rounded px-3 py-2 hover:bg-grey-5'
                    >
                      <div className='flex items-center gap-3'>
                        <input
                          type='checkbox'
                          checked={isSelected}
                          onChange={() => toggleDialogAsset(asset.id)}
                          className='h-4 w-4'
                        />
                        <div>
                          <p className='text-sm font-medium text-primary'>
                            {asset.name}
                          </p>
                          <p className='text-xs text-secondary'>{asset.type}</p>
                        </div>
                      </div>
                      <span className='text-xs text-secondary'>
                        {asset.deployment?.location?.name ||
                          t(
                            'job:assetDrivenTemplate.unknownLocation',
                            'Unknown'
                          )}
                      </span>
                    </label>
                  );
                })
              )}
            </div>
            <div className='mt-4 flex justify-end gap-2'>
              <button
                type='button'
                className='border-grey-200 rounded border px-4 py-2 text-sm font-medium text-secondary transition-colors hover:bg-grey-5'
                onClick={handleDialogCancel}
              >
                {t('translation:cancel', 'Cancel')}
              </button>
              <button
                type='button'
                className='rounded border border-brand bg-brand px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-darker'
                onClick={handleDialogSave}
              >
                {t('job:assetDrivenTemplate.applySelection', 'Apply Selection')}
              </button>
            </div>
          </div>
        </PopupDialog>
      )}
    </>
  );
};

/**
 * AssetDrivenAssetsSection - Separate component to render just the assets list section
 *
 * This component is extracted from AssetDrivenJobPreview to allow the parent form
 * to position the assets section in a different accordion section for better UX.
 *
 * Usage in CreateJobForm:
 * - Standard variant: Rendered before Tasks section, shows only uncovered assets
 * - Bulk variant: Rendered after template preview, shows all assets
 *
 * This separation allows:
 * - Better visual organization in the form
 * - Different positioning based on variant
 * - Reuse of the same asset display logic without duplicating template selection UI
 */
/**
 * Props for AssetDrivenAssetsSection component
 */
export type AssetDrivenAssetsSectionProps = {
  template: JobTemplateFieldsFragment;
  allAssets: Asset[];
  selectedAssetIds: string[];
  onAssetSelectionChange?: (assetIds: string[]) => void;
  onCreateAssetTask?: (assetId: string) => void;
  variant?: 'standard' | 'bulk';
  coveredAssetIds?: string[];
  showOnlyUncoveredAssets?: boolean;
  /** Show "Add Assets" button and dialog - only when accessed from assets (bulk asset flow) */
  showAddAssetsButton?: boolean;
  /** Assets that can be selected or added (defaults to allAssets) */
  availableAssets?: Asset[];
  /** Default location ID for asset selection */
  defaultLocationId?: string;
  /** Render prop for custom "Add Assets" button - receives openDialog function */
  renderAddAssetsButton?: (openDialog: () => void) => React.ReactNode;
  /** Callback to get the openDialog function - used when button is rendered outside component */
  onGetOpenDialog?: (openDialog: () => void) => void;
};

export const AssetDrivenAssetsSection = ({
  template,
  allAssets,
  selectedAssetIds,
  onAssetSelectionChange,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onCreateAssetTask: _onCreateAssetTask, // Not used - only showing assets with Other rule tasks
  variant = 'standard',
  coveredAssetIds = [],
  showOnlyUncoveredAssets = false,
  showAddAssetsButton = false,
  availableAssets,
  defaultLocationId,
  renderAddAssetsButton,
  onGetOpenDialog,
}: AssetDrivenAssetsSectionProps) => {
  const { t } = useTranslation();
  const isBulkVariant = variant === 'bulk';
  const [showAddAssetsDialog, setShowAddAssetsDialog] = useState(false);

  const openDialog = useCallback(() => {
    setShowAddAssetsDialog(true);
  }, []);

  // Expose openDialog function to parent if callback provided
  useEffect(() => {
    if (onGetOpenDialog && showAddAssetsButton && isBulkVariant) {
      onGetOpenDialog(openDialog);
    }
  }, [onGetOpenDialog, showAddAssetsButton, isBulkVariant, openDialog]);
  const [dialogSelection, setDialogSelection] =
    useState<string[]>(selectedAssetIds);
  const coveredAssetIdSet = useMemo(
    () => new Set(coveredAssetIds),
    [coveredAssetIds]
  );

  const selectableAssets = useMemo(
    () => availableAssets ?? allAssets,
    [allAssets, availableAssets]
  );

  useEffect(() => {
    if (showAddAssetsDialog) {
      setDialogSelection(selectedAssetIds);
    }
  }, [selectedAssetIds, showAddAssetsDialog]);

  // Get selected assets from IDs
  const selectedAssets = useMemo(() => {
    return allAssets.filter((asset) => selectedAssetIds.includes(asset.id));
  }, [allAssets, selectedAssetIds]);

  // Calculate task previews (we only need assetMatches, not tasks)
  const { assetMatches } = useMemo(
    () => calculateTaskPreviews(template, selectedAssets),
    [template, selectedAssets]
  );

  // For standard variant with showOnlyUncoveredAssets, filter to only show uncovered assets
  const assetsToDisplay = useMemo(() => {
    if (showOnlyUncoveredAssets && !isBulkVariant) {
      // Only show assets that don't have tasks created for them
      return assetMatches.filter(
        (m) => !m.hasMatchingTasks && !coveredAssetIdSet.has(m.asset.id)
      );
    }
    // For bulk variant or when showing all assets, return all asset matches
    return assetMatches;
  }, [showOnlyUncoveredAssets, isBulkVariant, assetMatches, coveredAssetIdSet]);

  // For bulk variant: show ALL selected assets
  // For standard variant: show only assets that have tasks created from the "Other" rule
  const displayedAssetsWithOtherRuleTasks = useMemo(() => {
    if (isBulkVariant) {
      // In bulk variant, show all assets (not just "Other" rule assets)
      return assetsToDisplay;
    }
    // In standard variant, only show assets with "Other" rule tasks
    return assetsToDisplay.filter(
      (m) =>
        m.matchingTasks.some((task) => task.usedOtherRule) &&
        !coveredAssetIdSet.has(m.asset.id)
    );
  }, [assetsToDisplay, isBulkVariant, coveredAssetIdSet]);

  // Handle removing an asset
  const handleRemoveAsset = (assetId: string) => {
    if (onAssetSelectionChange) {
      onAssetSelectionChange(selectedAssetIds.filter((id) => id !== assetId));
    }
  };

  // Assets for Add Assets dialog
  const assetsForAddDialog = useMemo(() => {
    if (isBulkVariant && defaultLocationId) {
      return selectableAssets.filter(
        (asset) => asset.deployment?.location?.id === defaultLocationId
      );
    }
    return selectableAssets;
  }, [defaultLocationId, isBulkVariant, selectableAssets]);

  const toggleDialogAsset = useCallback((assetId: string) => {
    setDialogSelection((prev) =>
      prev.includes(assetId)
        ? prev.filter((id) => id !== assetId)
        : [...prev, assetId]
    );
  }, []);

  const handleDialogCancel = useCallback(() => {
    setDialogSelection(selectedAssetIds);
    setShowAddAssetsDialog(false);
  }, [selectedAssetIds]);

  const handleDialogSave = useCallback(() => {
    if (onAssetSelectionChange) {
      onAssetSelectionChange(dialogSelection);
    }
    setShowAddAssetsDialog(false);
  }, [dialogSelection, onAssetSelectionChange]);

  if (selectedAssetIds.length === 0) return null;

  return (
    <>
      {/* Render custom heading with "Add Assets" button if provided - for bulk variant */}
      {renderAddAssetsButton &&
        showAddAssetsButton &&
        isBulkVariant &&
        onAssetSelectionChange &&
        renderAddAssetsButton(openDialog)}

      {/* Default "Add Assets" button when showAddAssetsButton is true but no render prop provided */}
      {!renderAddAssetsButton &&
        !onGetOpenDialog &&
        showAddAssetsButton &&
        isBulkVariant &&
        onAssetSelectionChange && (
          <div className='mb-4 flex items-center justify-end'>
            <button
              type='button'
              onClick={openDialog}
              className='hover:bg-brand/5 flex items-center gap-1.5 rounded border border-brand bg-white px-3 py-1.5 text-sm font-medium text-brand transition-colors'
            >
              <FontAwesomeIcon icon={faPlus} className='h-3.5 w-3.5' />
              {t('job:assetDrivenTemplate.addAssets', 'Add Assets')}
            </button>
          </div>
        )}

      <div className='space-y-2'>
        {/* For bulk variant: show streamlined cards with just name and remove button */}
        {/* For standard variant: show detailed cards with task information */}
        {displayedAssetsWithOtherRuleTasks.map(({ asset, matchingTasks }) => (
          <AssetCard
            key={asset.id}
            asset={asset}
            matchingTasks={matchingTasks}
            onRemoveAsset={
              onAssetSelectionChange ? handleRemoveAsset : undefined
            }
            variant={isBulkVariant ? 'streamlined' : 'withTasks'}
          />
        ))}
      </div>

      {/* Hint message for assets with Other rule tasks - only show in standard variant */}
      {!isBulkVariant && displayedAssetsWithOtherRuleTasks.length > 0 && (
        <p className='mt-2 text-xs text-secondary'>
          {t(
            'job:assetDrivenTemplate.assetsOtherRuleHint',
            'These assets matched the "Other" rule because they did not match any specific task rules.'
          )}
        </p>
      )}

      {/* Add Assets Dialog - only show when accessed from assets (bulk asset flow) */}
      {showAddAssetsDialog &&
        showAddAssetsButton &&
        isBulkVariant &&
        onAssetSelectionChange && (
          <PopupDialog
            isOpen={showAddAssetsDialog}
            onClose={handleDialogCancel}
            title={t(
              'job:assetDrivenTemplate.addMoreAssets',
              'Add More Assets'
            )}
          >
            <div className='p-6'>
              <p className='mb-4 text-sm text-secondary'>
                {t(
                  'job:assetDrivenTemplate.addMoreAssetsDescription',
                  'Select additional assets to include in this job. Tasks will be created based on the template rules.'
                )}
              </p>
              <div className='max-h-[50vh] overflow-y-auto rounded border border-grey-10 bg-white p-2'>
                {assetsForAddDialog.length === 0 ? (
                  <p className='text-sm text-secondary'>
                    {t(
                      'job:assetDrivenTemplate.noAssetsAtLocation',
                      'No assets are available at this location.'
                    )}
                  </p>
                ) : (
                  assetsForAddDialog.map((asset) => {
                    const isSelected = dialogSelection.includes(asset.id);
                    return (
                      <label
                        key={asset.id}
                        className='flex items-center justify-between rounded px-3 py-2 hover:bg-grey-5'
                      >
                        <div className='flex items-center gap-3'>
                          <input
                            type='checkbox'
                            checked={isSelected}
                            onChange={() => toggleDialogAsset(asset.id)}
                            className='h-4 w-4'
                          />
                          <div>
                            <p className='text-sm font-medium text-primary'>
                              {asset.name}
                            </p>
                            <p className='text-xs text-secondary'>
                              {asset.type}
                            </p>
                          </div>
                        </div>
                        <span className='text-xs text-secondary'>
                          {asset.deployment?.location?.name ||
                            t(
                              'job:assetDrivenTemplate.unknownLocation',
                              'Unknown'
                            )}
                        </span>
                      </label>
                    );
                  })
                )}
              </div>
              <div className='mt-4 flex justify-end gap-2'>
                <button
                  type='button'
                  className='border-grey-200 rounded border px-4 py-2 text-sm font-medium text-secondary transition-colors hover:bg-grey-5'
                  onClick={handleDialogCancel}
                >
                  {t('translation:cancel', 'Cancel')}
                </button>
                <button
                  type='button'
                  className='rounded border border-brand bg-brand px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-darker'
                  onClick={handleDialogSave}
                >
                  {t(
                    'job:assetDrivenTemplate.applySelection',
                    'Apply Selection'
                  )}
                </button>
              </div>
            </div>
          </PopupDialog>
        )}
    </>
  );
};
